// File: TitleRegistrationApplication.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './zoning-permit-form.css';

function TitleRegistrationApplication() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    // Reason(s)
    reasonNewTitle: false,
    reasonTransferTitle: false,
    reasonNewRegistration: false,
    reasonRenewal: false,
    reasonReplacement: false,
    reasonCorrection: false,

    // Owner (primary)
    ownerFirst: '',
    ownerMiddle: '',
    ownerLast: '',
    ownerDob: '',
    ownerDL: '',
    ownerEmail: '',
    ownerPhone: '',
    ownerAllowText: true,

    // Co-owner (optional)
    coOwnerFirst: '',
    coOwnerMiddle: '',
    coOwnerLast: '',
    coOwnerDL: '',

    // Address – Residential (required)
    resStreet: '',
    resApt: '',
    resCity: '',
    resState: '',
    resZip: '',

    // Mailing (optional or same-as)
    mailingSameAsResidential: true,
    mailStreet: '',
    mailApt: '',
    mailCity: '',
    mailState: '',
    mailZip: '',

    // Vehicle info
    vin: '',
    year: '',
    make: '',
    model: '',
    bodyStyle: '',
    color: '',
    fuelType: '',

    // Odometer
    odometerReading: '',
    odometerStatus: 'Actual', // Actual | NotActual | Exceeds | Exempt

    // Purchase / transfer details
    sellerType: 'Private',    // Private | Dealer
    purchaseDate: '',
    purchasePrice: '',
    tradeInAllowance: '',
    taxPaidToDealer: false,

    // Plate options
    plateOption: 'NewPlate', // NewPlate | TransferExisting | TempPermit
    transferPlateNumber: '',
    tempPermitStart: '',

    // Insurance
    insCarrier: '',
    insPolicyNumber: '',
    insExpiration: '',

    // Lien(s)
    hasLien: false,

    // Declaration
    certify: false,
    signature: '',
    signedDate: '',
  });

  const [liens, setLiens] = useState([{ name: '', street: '', city: '', state: '', zip: '' }]);

  // Helpers
  const onlyDigits = (s) => (s || '').replace(/\D+/g, '');
  const isFiveZip = (s) => /^\d{5}$/.test(s);
  const isTenPhone = (s) => /^\d{10}$/.test(onlyDigits(s));
  const isTwoLetterState = (s) => /^[A-Za-z]{2}$/.test(s);
  const isYear = (s) => /^\d{4}$/.test(s);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'mailingSameAsResidential') {
      const next = checked;
      setForm((f) => {
        const updated = { ...f, mailingSameAsResidential: next };
        if (next) {
          updated.mailStreet = f.resStreet;
          updated.mailApt = f.resApt;
          updated.mailCity = f.resCity;
          updated.mailState = f.resState.toUpperCase();
          updated.mailZip = f.resZip;
        }
        return updated;
      });
      setErrors((ep) => ({ ...ep, [name]: undefined }));
      return;
    }

    if (type === 'checkbox') {
      setForm((f) => ({ ...f, [name]: checked }));
      setErrors((ep) => ({ ...ep, [name]: undefined }));
      return;
    }

    // Uppercase any "*State" fields + VIN
    if (name.toLowerCase().endsWith('state')) {
      setForm((f) => ({ ...f, [name]: value.toUpperCase() }));
      setErrors((ep) => ({ ...ep, [name]: undefined }));
      return;
    }
    if (name === 'vin') {
      setForm((f) => ({ ...f, vin: value.toUpperCase().slice(0, 17) }));
      setErrors((ep) => ({ ...ep, vin: undefined }));
      return;
    }

    setForm((f) => ({ ...f, [name]: value }));
    setErrors((ep) => ({ ...ep, [name]: undefined }));
  };

  const updateLien = (idx, key, value) => {
    const copy = [...liens];
    copy[idx] = { ...copy[idx], [key]: key === 'state' ? String(value).toUpperCase() : value };
    setLiens(copy);
  };
  const addLien = () => setLiens([...liens, { name: '', street: '', city: '', state: '', zip: '' }]);
  const removeLien = (idx) => {
    const copy = [...liens];
    copy.splice(idx, 1);
    setLiens(copy.length ? copy : [{ name: '', street: '', city: '', state: '', zip: '' }]);
  };

  const validate = () => {
    const e = {};

    // Only require the first field (ownerFirst) for demo purposes
    if (!String(form.ownerFirst || '').trim()) {
      e.ownerFirst = 'Required';
    }

    // Keep format validation for fields that are filled in (but don't require them)
    if (form.ownerEmail && !/^\S+@\S+\.\S+$/.test(form.ownerEmail)) e.ownerEmail = 'Invalid email';
    if (form.ownerPhone && !isTenPhone(form.ownerPhone)) e.ownerPhone = '10 digits';

    if (form.resState && !isTwoLetterState(form.resState)) e.resState = 'Use 2-letter code';
    if (form.resZip && !isFiveZip(form.resZip)) e.resZip = '5-digit ZIP';

    if (!form.mailingSameAsResidential) {
      if (form.mailState && !isTwoLetterState(form.mailState)) e.mailState = 'Use 2-letter code';
      if (form.mailZip && !isFiveZip(form.mailZip)) e.mailZip = '5-digit ZIP';
    }

    // Vehicle format validation
    if (form.vin && form.vin.length !== 17) e.vin = 'VIN must be 17 characters';
    if (form.year && !isYear(form.year)) e.year = 'Use 4-digit year';

    // Odometer: allow blank only if Exempt
    if (form.odometerStatus !== 'Exempt') {
      if (form.odometerReading && isNaN(Number(form.odometerReading))) e.odometerReading = 'Numbers only';
    }

    // Purchase / transfer format validation
    if (form.purchasePrice && isNaN(Number(form.purchasePrice))) e.purchasePrice = 'Numbers only';
    if (form.tradeInAllowance && isNaN(Number(form.tradeInAllowance))) e.tradeInAllowance = 'Numbers only';

    // Lien format validation
    if (form.hasLien) {
      liens.forEach((ln, i) => {
        if (ln.state && !isTwoLetterState(ln.state)) e[`lienState_${i}`] = 'Use 2-letter code';
        if (ln.zip && !isFiveZip(ln.zip)) e[`lienZip_${i}`] = '5-digit ZIP';
      });
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
    }, 500);
  };

  const resetForm = () => window.location.reload();

  return (
    <section className="department-details-section">
      <div className="container gts-wide-container">
        <div className="row">
          <div className="col-12">
            {/* Header / Actions */}
            <div className="department-details-content-box">
              <h4 className="department-details-title">Vehicle Title & Registration</h4>
              <p className="gts-form-overview">
                Apply to title, register, renew, replace, or correct your vehicle record. This is a <strong>demo-only</strong> submission—
                no data is stored or transmitted.
              </p>
              <div className="gts-actions">
                <Link className="btn btn-secondary gts-toplink" to="/department/dmv">
                  DMV Info
                </Link>
              </div>
            </div>

            {/* Success screen */}
            {submitted ? (
              <div className="gts-preview success" role="status" aria-live="polite">
                <h5>🎉 Application submitted!</h5>
                <p>
                  Thank you. Your application has been recorded in this demo environment. A confirmation email would be sent in a live system.
                </p>
                <div className="gts-actions">
                  <button className="btn btn-secondary" onClick={() => window.print()}>Print this page</button>
                  <Link to="/forms" className="btn btn-secondary">Back to Forms &amp; Applications</Link>
                </div>
              </div>
            ) : (
              <form className="gts-form" onSubmit={onSubmit} noValidate>
                {/* Reasons */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Reason for Application</h5></div>
                  <div className="gts-form-grid">
                    {[
                      ['reasonNewTitle','New title (new to state)'],
                      ['reasonTransferTitle','Transfer title (sale/purchase)'],
                      ['reasonNewRegistration','New registration'],
                      ['reasonRenewal','Registration renewal'],
                      ['reasonReplacement','Replacement (lost/stolen/damaged)'],
                      ['reasonCorrection','Record correction (name, VIN, etc.)'],
                    ].map(([name, label]) => (
                      <label key={name} className="gts-field gts-span-2 gts-checkboxline">
                        <input type="checkbox" name={name} checked={form[name]} onChange={onChange} />
                        <span>{label}</span>
                      </label>
                    ))}
                    {errors.reasons && <em className="gts-error">{errors.reasons}</em>}
                  </div>
                </div>

                {/* Owner */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Owner Information</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field"><span>First Name <span className="gts-required">*</span></span><input name="ownerFirst" value={form.ownerFirst} onChange={onChange} />{errors.ownerFirst && <em className="gts-error">{errors.ownerFirst}</em>}</label>
                    <label className="gts-field"><span>Middle</span><input name="ownerMiddle" value={form.ownerMiddle} onChange={onChange} /></label>
                    <label className="gts-field"><span>Last Name <span className="gts-required">*</span></span><input name="ownerLast" value={form.ownerLast} onChange={onChange} />{errors.ownerLast && <em className="gts-error">{errors.ownerLast}</em>}</label>
                    <label className="gts-field"><span>Date of Birth <span className="gts-required">*</span></span><input type="date" name="ownerDob" value={form.ownerDob} onChange={onChange} />{errors.ownerDob && <em className="gts-error">{errors.ownerDob}</em>}</label>
                    <label className="gts-field"><span>Driver License # <span className="gts-required">*</span></span><input name="ownerDL" value={form.ownerDL} onChange={onChange} />{errors.ownerDL && <em className="gts-error">{errors.ownerDL}</em>}</label>
                    <label className="gts-field"><span>Email <span className="gts-required">*</span></span><input type="email" name="ownerEmail" value={form.ownerEmail} onChange={onChange} />{errors.ownerEmail && <em className="gts-error">{errors.ownerEmail}</em>}</label>
                    <label className="gts-field"><span>Phone</span><input name="ownerPhone" value={form.ownerPhone} onChange={onChange} maxLength={14} />{errors.ownerPhone && <em className="gts-error">{errors.ownerPhone}</em>}</label>
                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input type="checkbox" name="ownerAllowText" checked={form.ownerAllowText} onChange={onChange} />
                      <span>Okay to text me about DMV updates</span>
                    </label>
                  </div>
                </div>

                {/* Co-owner (optional) */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Co-Owner (Optional)</h5><div className="gts-small-note">Leave blank if none</div></div>
                  <div className="gts-form-grid">
                    <label className="gts-field"><span>First Name</span><input name="coOwnerFirst" value={form.coOwnerFirst} onChange={onChange} /></label>
                    <label className="gts-field"><span>Middle</span><input name="coOwnerMiddle" value={form.coOwnerMiddle} onChange={onChange} /></label>
                    <label className="gts-field"><span>Last Name</span><input name="coOwnerLast" value={form.coOwnerLast} onChange={onChange} /></label>
                    <label className="gts-field"><span>Driver License #</span><input name="coOwnerDL" value={form.coOwnerDL} onChange={onChange} /></label>
                  </div>
                </div>

                {/* Address */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Address</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field gts-span-2"><span>Residential Street <span className="gts-required">*</span></span><input name="resStreet" value={form.resStreet} onChange={onChange} /></label>
                    <label className="gts-field"><span>Apt/Suite</span><input name="resApt" value={form.resApt} onChange={onChange} /></label>
                    <label className="gts-field"><span>City <span className="gts-required">*</span></span><input name="resCity" value={form.resCity} onChange={onChange} /></label>
                    <label className="gts-field"><span>State (2) <span className="gts-required">*</span></span><input name="resState" value={form.resState} onChange={onChange} maxLength={2} placeholder="TN" />{errors.resState && <em className="gts-error">{errors.resState}</em>}</label>
                    <label className="gts-field"><span>ZIP (5) <span className="gts-required">*</span></span><input name="resZip" value={form.resZip} onChange={onChange} maxLength={5} />{errors.resZip && <em className="gts-error">{errors.resZip}</em>}</label>
                    {errors.resAddress && <em className="gts-error">{errors.resAddress}</em>}

                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input type="checkbox" name="mailingSameAsResidential" checked={form.mailingSameAsResidential} onChange={onChange} />
                      <span>Mailing address is the same as residential</span>
                    </label>

                    {!form.mailingSameAsResidential && (
                      <>
                        <label className="gts-field gts-span-2"><span>Mailing Street</span><input name="mailStreet" value={form.mailStreet} onChange={onChange} /></label>
                        <label className="gts-field"><span>Apt/Suite</span><input name="mailApt" value={form.mailApt} onChange={onChange} /></label>
                        <label className="gts-field"><span>City</span><input name="mailCity" value={form.mailCity} onChange={onChange} /></label>
                        <label className="gts-field"><span>State (2)</span><input name="mailState" value={form.mailState} onChange={onChange} maxLength={2} /></label>
                        <label className="gts-field"><span>ZIP (5)</span><input name="mailZip" value={form.mailZip} onChange={onChange} maxLength={5} /></label>
                        {errors.mailState && <em className="gts-error">{errors.mailState}</em>}
                        {errors.mailZip && <em className="gts-error">{errors.mailZip}</em>}
                      </>
                    )}
                  </div>
                </div>

                {/* Vehicle info */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Vehicle Information</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field gts-span-2"><span>VIN (17) <span className="gts-required">*</span></span><input name="vin" value={form.vin} onChange={onChange} maxLength={17} />{errors.vin && <em className="gts-error">{errors.vin}</em>}</label>
                    <label className="gts-field"><span>Year <span className="gts-required">*</span></span><input name="year" value={form.year} onChange={onChange} maxLength={4} />{errors.year && <em className="gts-error">{errors.year}</em>}</label>
                    <label className="gts-field"><span>Make <span className="gts-required">*</span></span><input name="make" value={form.make} onChange={onChange} />{errors.make && <em className="gts-error">{errors.make}</em>}</label>
                    <label className="gts-field"><span>Model <span className="gts-required">*</span></span><input name="model" value={form.model} onChange={onChange} />{errors.model && <em className="gts-error">{errors.model}</em>}</label>
                    <label className="gts-field"><span>Body Style</span><input name="bodyStyle" value={form.bodyStyle} onChange={onChange} /></label>
                    <label className="gts-field"><span>Color</span><input name="color" value={form.color} onChange={onChange} /></label>
                    <label className="gts-field"><span>Fuel</span>
                      <select name="fuelType" value={form.fuelType} onChange={onChange}>
                        <option value="">Select…</option>
                        <option>Gasoline</option>
                        <option>Diesel</option>
                        <option>Hybrid</option>
                        <option>Electric</option>
                        <option>Other</option>
                      </select>
                    </label>
                  </div>
                </div>

                {/* Odometer */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Odometer Disclosure</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field">
                      <span>Reading (miles)</span>
                      <input name="odometerReading" value={form.odometerReading} onChange={onChange} />
                      {errors.odometerReading && <em className="gts-error">{errors.odometerReading}</em>}
                    </label>
                    <fieldset className="gts-field">
                      <legend>Status</legend>
                      <div className="gts-radio-list">
                        {['Actual','NotActual','Exceeds','Exempt'].map((opt) => (
                          <label className="gts-radio" key={opt}>
                            <input type="radio" name="odometerStatus" value={opt} checked={form.odometerStatus===opt} onChange={onChange} />
                            <span>{opt === 'NotActual' ? 'Not actual' : opt === 'Exceeds' ? 'Exceeds mechanical limits' : opt}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  </div>
                </div>

                {/* Purchase / transfer */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Purchase / Transfer</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field"><span>Seller Type</span>
                      <select name="sellerType" value={form.sellerType} onChange={onChange}>
                        <option>Private</option>
                        <option>Dealer</option>
                      </select>
                    </label>
                    <label className="gts-field"><span>Purchase Date{(form.reasonNewTitle || form.reasonTransferTitle) && <span className="gts-required"> *</span>}</span>
                      <input type="date" name="purchaseDate" value={form.purchaseDate} onChange={onChange} />
                      {errors.purchaseDate && <em className="gts-error">{errors.purchaseDate}</em>}
                    </label>
                    <label className="gts-field"><span>Purchase Price ($){(form.reasonNewTitle || form.reasonTransferTitle) && <span className="gts-required"> *</span>}</span>
                      <input name="purchasePrice" value={form.purchasePrice} onChange={onChange} />
                      {errors.purchasePrice && <em className="gts-error">{errors.purchasePrice}</em>}
                    </label>
                    <label className="gts-field"><span>Trade-in Allowance ($)</span><input name="tradeInAllowance" value={form.tradeInAllowance} onChange={onChange} />{errors.tradeInAllowance && <em className="gts-error">{errors.tradeInAllowance}</em>}</label>
                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input type="checkbox" name="taxPaidToDealer" checked={form.taxPaidToDealer} onChange={onChange} />
                      <span>Sales tax paid to dealer</span>
                    </label>
                  </div>
                </div>

                {/* Plate options */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Plate Options</h5></div>
                  <div className="gts-form-grid">
                    <fieldset className="gts-field gts-span-2">
                      <legend>Choose one</legend>
                      <div className="gts-radio-list">
                        {[
                          ['NewPlate','Issue new plate'],
                          ['TransferExisting','Transfer existing plate'],
                          ['TempPermit','Temporary 30-day permit'],
                        ].map(([value, label]) => (
                          <label className="gts-radio" key={value}>
                            <input type="radio" name="plateOption" value={value} checked={form.plateOption===value} onChange={onChange} />
                            <span>{label}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    {form.plateOption === 'TransferExisting' && (
                      <label className="gts-field">
                        <span>Plate to Transfer <span className="gts-required">*</span></span>
                        <input name="transferPlateNumber" value={form.transferPlateNumber} onChange={onChange} />
                        {errors.transferPlateNumber && <em className="gts-error">{errors.transferPlateNumber}</em>}
                      </label>
                    )}

                    {form.plateOption === 'TempPermit' && (
                      <label className="gts-field">
                        <span>Temp Permit Start Date <span className="gts-required">*</span></span>
                        <input type="date" name="tempPermitStart" value={form.tempPermitStart} onChange={onChange} />
                        {errors.tempPermitStart && <em className="gts-error">{errors.tempPermitStart}</em>}
                      </label>
                    )}
                  </div>
                </div>

                {/* Insurance */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Insurance</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field"><span>Carrier <span className="gts-required">*</span></span><input name="insCarrier" value={form.insCarrier} onChange={onChange} />{errors.insCarrier && <em className="gts-error">{errors.insCarrier}</em>}</label>
                    <label className="gts-field"><span>Policy # <span className="gts-required">*</span></span><input name="insPolicyNumber" value={form.insPolicyNumber} onChange={onChange} />{errors.insPolicyNumber && <em className="gts-error">{errors.insPolicyNumber}</em>}</label>
                    <label className="gts-field"><span>Expiration <span className="gts-required">*</span></span><input type="date" name="insExpiration" value={form.insExpiration} onChange={onChange} />{errors.insExpiration && <em className="gts-error">{errors.insExpiration}</em>}</label>
                  </div>
                </div>

                {/* Lienholders */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Lienholders</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input type="checkbox" name="hasLien" checked={form.hasLien} onChange={onChange} />
                      <span>A lienholder is listed on this vehicle</span>
                    </label>
                    {errors.lien && <em className="gts-error">{errors.lien}</em>}
                  </div>

                  {form.hasLien && (
                    <div className="gts-panel gts-panel-margin-top">
                      {liens.map((ln, idx) => (
                        <div key={idx} className="gts-form-grid" style={{ marginBottom: 8 }}>
                          <label className="gts-field gts-span-2"><span>Lienholder Name</span><input value={ln.name} onChange={(e)=>updateLien(idx,'name',e.target.value)} /></label>
                          <label className="gts-field gts-span-2"><span>Street</span><input value={ln.street} onChange={(e)=>updateLien(idx,'street',e.target.value)} /></label>
                          <label className="gts-field"><span>City</span><input value={ln.city} onChange={(e)=>updateLien(idx,'city',e.target.value)} /></label>
                          <label className="gts-field"><span>State (2)</span><input value={ln.state} onChange={(e)=>updateLien(idx,'state',e.target.value)} maxLength={2} /></label>
                          <label className="gts-field"><span>ZIP (5)</span><input value={ln.zip} onChange={(e)=>updateLien(idx,'zip',e.target.value)} maxLength={5} /></label>
                          {(errors[`lienState_${idx}`] || errors[`lienZip_${idx}`]) && (
                            <em className="gts-error">
                              {errors[`lienState_${idx}`] || errors[`lienZip_${idx}`]}
                            </em>
                          )}
                          <div className="gts-field" style={{ alignItems: 'flex-start', justifyContent: 'flex-end' }}>
                            <button type="button" className="btn btn-outline-primary" onClick={()=>removeLien(idx)}>Remove</button>
                          </div>
                        </div>
                      ))}
                      <div className="gts-actions">
                        <button type="button" className="btn btn-secondary" onClick={addLien}>Add Lienholder</button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Declaration */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Declaration &amp; Signature</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input type="checkbox" name="certify" checked={form.certify} onChange={onChange} />
                      <span>
                        I certify the information is true and complete, and I understand this is a demo submission (no data transmitted).
                        <span className="gts-required"> *</span>
                      </span>
                      {errors.certify && <em className="gts-error">{errors.certify}</em>}
                    </label>
                    <label className="gts-field"><span>Signature (type full name) <span className="gts-required">*</span></span><input name="signature" value={form.signature} onChange={onChange} />{errors.signature && <em className="gts-error">{errors.signature}</em>}</label>
                    <label className="gts-field"><span>Date <span className="gts-required">*</span></span><input type="date" name="signedDate" value={form.signedDate} onChange={onChange} />{errors.signedDate && <em className="gts-error">{errors.signedDate}</em>}</label>
                  </div>
                </div>

                {/* Actions */}
                <div className="gts-actions gts-actions-form">
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? 'Submitting…' : 'Submit'}
                  </button>
                  <button type="button" className="btn btn-outline-primary" onClick={resetForm} disabled={submitting}>
                    Reset Form
                  </button>
                </div>
              </form>
            )}

            <div style={{ marginTop: 12 }}>
              <Link to="/forms" className="gts-muted-link">
                <i className="fa-solid fa-circle-arrow-left" style={{ marginRight: 5 }} />
                Back to Forms &amp; Applications
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default TitleRegistrationApplication;
